import { logError } from '@ptek/client-logger';
import { apiRouter } from '../config/api';
import {
  EnqueuedGeneration,
  GeneratedDocument,
  GenerateDocumentRequest,
  GenerateDocumentResponse,
  ScribeJobStatus,
  SessionDocumentsResponse,
} from '../types/document';
import { DocumentTemplate } from '../types/templates';
import { detectPipelineVersion } from '../utils/templateUtils';

export class DocumentTemplatesClient {
  /**
   * Make API call using the router that automatically directs to legacy or new service
   */
  private async makeApiCall(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<unknown> {
    return apiRouter.makeApiCall(endpoint, options);
  }

  /**
   * Fetch all available document templates
   * GET /v1/templates
   */
  async getTemplates(filters?: {
    tenantId?: string;
    documentType?: string;
    activeOnly?: boolean;
  }): Promise<DocumentTemplate[]> {
    const params = new URLSearchParams();
    if (filters?.documentType)
      params.append('documentType', filters.documentType);
    if (filters?.activeOnly !== undefined)
      params.append('activeOnly', filters.activeOnly.toString());

    const queryString = params.toString();
    const endpoint = `/api/v1/templates${queryString ? `?${queryString}` : ''}`;

    // Backend returns { templates: [...], filters: {...} } where templates are already in DocumentTemplate format
    const data = (await this.makeApiCall(endpoint)) as {
      templates: DocumentTemplate[];
      filters: {
        customerId: string;
        documentType: string | null;
        activeOnly: boolean;
      };
    };

    return data.templates || [];
  }

  /**
   * Fetch all available document templates (v2 — platform-owned table with ScopeIndex)
   * GET /v2/templates
   */
  async getTemplatesV2(filters?: {
    documentType?: string;
    activeOnly?: boolean;
  }): Promise<DocumentTemplate[]> {
    const params = new URLSearchParams();
    if (filters?.documentType)
      params.append('documentType', filters.documentType);
    if (filters?.activeOnly !== undefined)
      params.append('activeOnly', filters.activeOnly.toString());

    const queryString = params.toString();
    const endpoint = `/api/v2/templates${queryString ? `?${queryString}` : ''}`;

    const data = (await this.makeApiCall(endpoint)) as {
      templates: DocumentTemplate[];
    };

    return data.templates || [];
  }

  /**
   * Get a specific template by ID
   * GET /v1/templates/{id}
   */
  async getTemplate(id: string): Promise<DocumentTemplate | null> {
    try {
      // Backend returns { template: {...} }
      const data = (await this.makeApiCall(`/api/v1/templates/${id}`)) as {
        template: DocumentTemplate;
      };

      return data.template || null;
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }

      return null;
    }
  }

  /**
   * Generate documents from template
   * POST /v1/documents/generate-from-template (V1 pipeline)
   * POST /v2/documents/generate-from-template (V2 pipeline)
   * POST /v3/documents/generate-from-template (V3 pipeline)
   *
   * Routes to appropriate endpoint based on template pipeline version
   */
  async generateDocuments(
    request: GenerateDocumentRequest
  ): Promise<GeneratedDocument | EnqueuedGeneration | null> {
    const pipelineVersion = detectPipelineVersion(request.template);

    let endpoint: string;
    switch (pipelineVersion) {
      case 'v2':
        endpoint = '/api/v2/documents/generate-from-template';
        break;
      case 'v3':
        endpoint = '/api/v3/documents/generate-from-template';
        break;
      default:
        endpoint = '/api/v1/documents/generate-from-template';
        break;
    }

    const requestBody = {
      sessionId: request.sessionId,
      template: request.template,
      userId: request.userId,
      backgroundGeneration: request.backgroundGeneration ?? false,
      ...(request.unifiedSessionId && { unifiedSessionId: true }),
      ...(request.features &&
        Object.keys(request.features).length > 0 && {
          features: request.features,
        }),
      context: request.context,
      widgetConfig: request.widgetConfig,
      contextItems: request.contextItems,
      ...(request.transcript && { transcript: request.transcript }),
    };

    try {
      const data = (await this.makeApiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify(requestBody),
        // Forward the abort signal (if any) so the timeout owner can abort
        // a hung generate-from-template request. Optional for all callers.
        ...(request.signal && { signal: request.signal }),
      })) as GenerateDocumentResponse;

      if (data.documents && data.documents.length > 0) {
        const document = data.documents[0];

        const formattedDocument: GeneratedDocument = {
          id: document.documentId,
          templateId: document.templateId,
          content:
            typeof document.generatedContent === 'string'
              ? document.generatedContent
              : JSON.stringify(document.generatedContent || ''),
          status: 'completed',
          createdAt:
            typeof document.createdAt === 'string'
              ? document.createdAt
              : new Date().toISOString(),
          ...(data.job_id && { jobId: data.job_id }),
        };

        return formattedDocument;
      }

      if (data.job_id) {
        return { status: 'enqueued', jobId: data.job_id };
      }

      return null;
    } catch (error) {
      logError(
        'DocumentTemplatesClient',
        'generateDocuments API call failed:',
        error
      );
      throw error;
    }
  }

  /**
   * Read the status of the background scribe-generation job linked to a session.
   * POST /v1/jobs/status-by-entity
   *
   * The job is entity-linked on the docgen sessionId (NEB-98752), so on a cold
   * reopen the widget can recover whether a generation is still running, has
   * completed, or has failed — without a live WS connection. Returns the
   * platform's verbatim status literal, or `null` when no job is linked (or the
   * read fails — the caller treats `null` as "nothing in flight").
   */
  async getScribeJobStatusBySession(
    sessionId: string
  ): Promise<ScribeJobStatus> {
    try {
      const data = (await this.makeApiCall('/api/v1/jobs/status-by-entity', {
        method: 'POST',
        body: JSON.stringify({
          job_type: 'scribe-generation',
          entity_type: 'session',
          entity_ids: [sessionId],
        }),
      })) as {
        results?: Array<{ entity_id: string; status: ScribeJobStatus }>;
      };

      const match = data.results?.find((r) => r.entity_id === sessionId);
      return match?.status ?? null;
    } catch (error) {
      logError(
        'DocumentTemplatesClient',
        'Error reading scribe job status by session:',
        error
      );
      return null;
    }
  }

  async getScribeJobStatus(jobId: string): Promise<ScribeJobStatus> {
    try {
      const data = (await this.makeApiCall(`/api/v1/jobs/${jobId}/status`, {
        method: 'GET',
      })) as { status?: ScribeJobStatus };
      return data.status ?? null;
    } catch (error) {
      logError(
        'DocumentTemplatesClient',
        'Error reading scribe job status:',
        error
      );
      return null;
    }
  }

  /**
   * Fetch documents for a specific session
   * GET /v1/sessions/{id}/documents
   */
  async getSessionDocuments(
    sessionId: string
  ): Promise<SessionDocumentsResponse> {
    try {
      const data = (await this.makeApiCall(
        `/api/v1/sessions/${sessionId}/documents`,
        { method: 'GET' }
      )) as SessionDocumentsResponse;

      return data;
    } catch (error) {
      logError(
        'DocumentTemplatesClient',
        'Error getting session documents:',
        error
      );
      return {
        documents: [],
        sessionId: sessionId,
        totalCount: 0,
      };
    }
  }
}

export const documentTemplatesClient = new DocumentTemplatesClient();
